import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Upload,
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Search,
  Plus,
  AlertTriangle,
  DollarSign,
  Trash2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import ProductCreationDrawer from './ProductCreationDrawer';
import EnhancedProductLookup from './EnhancedProductLookup';
import salesImportService from '../services/salesImportService';
import ClientSelector from './ClientSelector';

const SalesImportDialog = ({
  open,
  onClose,
  mode = 'sales_order',
  onImportComplete
}) => {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);

  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [currentCreateItem, setCurrentCreateItem] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [pricingMode, setPricingMode] = useState('tax_exclusive');

  const [manualMatchDialog, setManualMatchDialog] = useState({
    open: false,
    itemIndex: null,
    item: null
  });

  const [orderId, setOrderId] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleExtract = async () => {
    if (!file) {
      setError('请先选择文件');
      return;
    }

    setExtracting(true);
    setError(null);

    try {
      const result = await salesImportService.extractFromFile(file, mode);
      
      if (result.success) {
        setExtractedData(result);
        
        const preview = await salesImportService.getPreview(result);
        if (preview.success) {
          setPreviewData(preview);
          setStep('preview');
        } else {
          setError(preview.error || '预览失败');
        }
      } else {
        setError(result.error || '提取失败');
      }
    } catch (err) {
      setError(err.message || '提取过程中发生错误');
    } finally {
      setExtracting(false);
    }
  };

  const handleClientChange = useCallback((clientId, clientData) => {
    if (clientId && clientData) {
      setSelectedClient({
        client_id: clientId,
        client_ref: clientData.client_code || clientData.client_id || '',
        name: clientData.name || clientData.client_name || ''
      });
    } else {
      setSelectedClient(null);
    }
  }, []);

  const handleOpenCreateDrawer = (item, itemIndex) => {
    setCurrentCreateItem({ ...item, _itemIndex: itemIndex });
    setShowCreateDrawer(true);
  };

  const handleOpenManualMatch = (item, itemIndex) => {
    setManualMatchDialog({
      open: true,
      itemIndex,
      item,
      searchQuery: item.product_id || item.specification || ''
    });
  };

  const handleManualMatch = (selectedProductsList) => {
    if (selectedProductsList.length === 0) return;
    
    const newProduct = selectedProductsList[0];
    const { itemIndex } = manualMatchDialog;
    
    if (newProduct._id) {
      // Update selected products mapping
      setSelectedProducts(prev => ({
        ...prev,
        [itemIndex]: newProduct._id
      }));
      
      // Update previewData with matched product info
      setPreviewData(prev => {
        if (!prev?.items) return prev;
        const newItems = [...prev.items];
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          _id: newProduct._id,  // Critical: store the product MongoDB _id
          product_id: newProduct.product_id || newItems[itemIndex].product_id,
          product_name: newProduct.name,
          specification: newProduct.specification || newItems[itemIndex].specification,
          // Preserve original OCR price, do NOT overwrite with database price
          unit_price: newItems[itemIndex].unit_price, 
          match_status: 'exact',
          matches: [newProduct]
        };
        return { ...prev, items: newItems };
      });
      
      // Auto-confirm after matching
      setConfirmedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(itemIndex);
        return newSet;
      });
    }
    
    setManualMatchDialog({ open: false, itemIndex: null, item: null });
  };

  const handleProductCreated = async (newProduct) => {
    if (!previewData || !currentCreateItem) return;

    const itemIndex = currentCreateItem._itemIndex;
    if (itemIndex < 0) return;

    try {
      setSelectedProducts(prev => ({
        ...prev,
        [itemIndex]: newProduct._id
      }));

      setPreviewData(prev => {
        if (!prev?.items) return prev;
        const newItems = [...prev.items];
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          _id: newProduct._id,
          product_id: newProduct.product_id || newItems[itemIndex].product_id,
          product_name: newProduct.name,
          specification: newProduct.specification || newItems[itemIndex].specification,
          unit_price: newItems[itemIndex].unit_price,
          match_status: 'exact',
          matches: [{
            _id: newProduct._id,
            product_id: newProduct.product_id,
            name: newProduct.name,
            specification: newProduct.specification,
            unit_price: newProduct.price || 0
          }]
        };
        return { ...prev, items: newItems };
      });

      setConfirmedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(itemIndex);
        return newSet;
      });
    } catch (err) {
      console.error('Failed to update preview:', err);
    } finally {
      setShowCreateDrawer(false);
      setCurrentCreateItem(null);
    }
  };

  const [confirmedItems, setConfirmedItems] = useState(new Set());

  const canConfirm = () => {
    if (!selectedClient?.client_id) return false;
    if (!previewData?.items?.length) return false;
    if (previewData.items.length === 0) return false;
    
    return confirmedItems.size === previewData.items.length;
  };

  const handleConfirmItem = (idx) => {
    setConfirmedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(idx);
      return newSet;
    });
  };

  const handleConfirmAll = () => {
    if (!previewData?.items?.length) return;
    
    // Auto-confirm items with matches, require manual for others
    const toConfirm = new Set();
    previewData.items.forEach((item, idx) => {
      const hasMatch = item.matches?.length > 0;
      if (hasMatch) {
        toConfirm.add(idx);
        // Auto-select first match
        setSelectedProducts(prev => ({
          ...prev,
          [idx]: item.matches[0]._id
        }));
      }
    });
    setConfirmedItems(toConfirm);
  };

  const handleUnconfirmItem = (idx) => {
    setConfirmedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(idx);
      return newSet;
    });
  };

  const handleDeleteItem = (idx) => {
    setPreviewData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
    setSelectedProducts(prev => {
      const newSet = { ...prev };
      Object.keys(newSet).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum === idx) {
          delete newSet[key];
        } else if (keyNum > idx) {
          const newKey = String(keyNum - 1);
          newSet[newKey] = newSet[key];
          delete newSet[key];
        }
      });
      return newSet;
    });
    setConfirmedItems(prev => {
      const newSet = new Set();
      prev.forEach(itemIdx => {
        if (itemIdx < idx) {
          newSet.add(itemIdx);
        } else if (itemIdx > idx) {
          newSet.add(itemIdx - 1);
        }
      });
      return newSet;
    });
  };

  const handleConfirm = async () => {
    if (!canConfirm() || !previewData) return;

    setCreatingOrder(true);
    setError(null);

    try {
      const orderItems = previewData.items.map((item, idx) => {
        const selectedId = selectedProducts[idx] || (item.match_status === 'exact' || item.match_status === 'by_spec' ? item.matches[0]?._id : null);
        const match = item.matches?.find(m => m._id === selectedId) || {};
        
        const ocr_price = item.unit_price || match.unit_price || 0;
        const tax_rate = item.tax_rate || 0.13;
        
        // Calculate prices based on pricing mode
        const unit_price = pricingMode === 'tax_inclusive'
          ? ocr_price / (1 + tax_rate)
          : ocr_price;
        const unit_price_tax_included = pricingMode === 'tax_inclusive'
          ? ocr_price
          : ocr_price * (1 + tax_rate);
        
        const quantity = item.quantity || 1;
        
        return {
          _id: selectedId,
          product_id: item.product_id || match.product_id || '',
          name: match.name || item.name || '',
          specification: item.specification || match.specification || '',
          quantity: quantity,
          unit: item.unit || '个',
          tax_rate: tax_rate,
          unit_price: unit_price,
          price: unit_price,
          unit_price_tax_included: unit_price_tax_included,
          total_price: Math.round(unit_price * quantity * 100) / 100,
          total_price_tax_included: Math.round(unit_price_tax_included * quantity * 100) / 100
        };
      });

      const orderData = {
        sales_order_id: orderId || undefined,
        client_id: selectedClient.client_id,
        client_name: selectedClient.name,
        client_ref: selectedClient.client_ref,
        items: orderItems,
        order_date: orderDate,
        delivery_date: extractedData?.delivery_date || null,
        notes: extractedData?.notes || '',
        pricing_mode: pricingMode,
        total_price: orderItems.reduce((sum, item) => sum + (item.total_price || 0), 0),
        total_price_tax_included: orderItems.reduce((sum, item) => sum + (item.total_price_tax_included || 0), 0)
      };

      let result;
      if (mode === 'sales_order') {
        result = await salesImportService.importSalesOrder(orderData);
      } else {
        result = await salesImportService.importQuotation(orderData);
      }

      if (result.success) {
        setStep('complete');
        if (onImportComplete) {
          onImportComplete(result);
        }
      } else {
        setError(result.error || '创建失败');
      }
    } catch (err) {
      setError(err.message || '创建过程中发生错误');
    } finally {
      setCreatingOrder(false);
    }
  };

  const calculateTotal = () => {
    if (!previewData) return 0;
    const taxRate = 0.13;

    return previewData.items.reduce((sum, item, idx) => {
      const selectedId = selectedProducts[idx] || (item.match_status === 'exact' || item.match_status === 'by_spec' ? item.matches[0]?._id : null);
      const match = item.matches?.find(m => m._id === selectedId);
      const ocrPrice = item.unit_price || match?.unit_price || 0;
      // For total, use pre-tax price
      const unitPrice = pricingMode === 'tax_inclusive' 
        ? ocrPrice / (1 + taxRate)
        : ocrPrice;
      return sum + (unitPrice * (item.quantity || 1));
    }, 0);
  };

  const resetDialog = () => {
    setStep('upload');
    setFile(null);
    setExtractedData(null);
    setPreviewData(null);
    setSelectedProducts({});
    setSelectedClient(null);
    setError(null);
    setOrderId('');
    setOrderDate(new Date().toISOString().split('T')[0]);
    setConfirmedItems(new Set());
    setPricingMode('tax_exclusive');
    setManualMatchDialog({ open: false, itemIndex: null, item: null, searchQuery: '' });
    setShowCreateDrawer(false);
    setCurrentCreateItem(null);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['xlsx', 'xls'].includes(ext)) {
      return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
    } else if (ext === 'pdf') {
      return <FileText className="h-8 w-8 text-red-600" />;
    } else if (['jpg', 'jpeg', 'png'].includes(ext)) {
      return <ImageIcon className="h-8 w-8 text-blue-600" />;
    }
    return <FileText className="h-8 w-8 text-gray-600" />;
  };

  const getMatchStatusBadge = (status, item, idx) => {
    switch (status) {
      case 'exact':
        return <Badge variant="success" className="gap-1 cursor-pointer" onClick={() => handleOpenManualMatch(item, idx)}><CheckCircle className="h-3 w-3" />已匹配</Badge>;
      case 'by_spec':
        return <Badge variant="warning" className="gap-1 cursor-pointer" onClick={() => handleOpenManualMatch(item, idx)}>待确认</Badge>;
      case 'multiple':
        return <Badge variant="warning" className="gap-1 cursor-pointer" onClick={() => handleOpenManualMatch(item, idx)}>待选择</Badge>;
      case 'by_spec_multiple':
        return <Badge variant="warning" className="gap-1 cursor-pointer" onClick={() => handleOpenManualMatch(item, idx)}>按规格待选择</Badge>;
      case 'none':
        return <Badge variant="secondary" className="gap-1">需处理</Badge>;
      case 'conflict':
        return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />规格冲突</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `¥${Number(amount).toLocaleString()}`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose} modal={true}>
        <DialogContent
          className="max-w-[1200px] w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            // 只有在初始上传步骤才允许按ESC关闭
            if (step !== 'upload') {
              e.preventDefault();
            }
          }}
        >

          {step === 'upload' && (
            <div className="flex-1 overflow-auto p-6">
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-12 text-center">
                {file ? (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      {getFileIcon(file.name)}
                    </div>
                    <div className="font-medium">{file.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(2)} KB
                    </div>
                    <Button variant="outline" onClick={() => setFile(null)}>
                      更换文件
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground/50" />
                    <div className="text-lg font-medium">
                      拖拽文件到此处，或点击选择文件
                    </div>
                    <div className="text-sm text-muted-foreground">
                      支持 Excel (.xlsx, .xls)、PDF、图片格式
                    </div>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <Button asChild>
                      <label htmlFor="file-upload" className="cursor-pointer">
                        选择文件
                      </label>
                    </Button>
                  </div>
                )}
              </div>

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end mt-6">
                <Button
                  onClick={handleExtract}
                  disabled={!file || extracting}
                  className="gap-2"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      正在提取...
                    </>
                  ) : (
                    <>
                      开始提取
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === 'preview' && previewData && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setStep('upload')}
                      className="gap-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      上一步
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      共 {previewData.items?.length || 0} 个产品
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    {selectedClient ? (
                      <Badge variant="outline" className="gap-1">
                        {selectedClient.name}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">请选择客户</Badge>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">总计:</span>
                      <span className="text-xl font-bold text-primary">
                        {formatCurrency(calculateTotal())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-auto px-6 py-4">
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* 客户选择 */}
                <div className="mb-6">
                  <label className="text-sm font-medium mb-2 block">客户选择 *</label>
                  <ClientSelector
                    selectedClientId={selectedClient?.client_id}
                    onClientChange={handleClientChange}
                    selectedClientName={previewData.client?.name || extractedData?.client_name || ''}
                    required
                    label=""
                    placeholder="搜索客户..."
                    compact={false}
                    size="medium"
                  />
                </div>

                {/* 价格模式选择 */}
                <div className="mb-4 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">价格模式:</label>
                    <Select value={pricingMode} onValueChange={setPricingMode}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tax_exclusive">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            <span>未税价格</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="tax_inclusive">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            <span>含税价格</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {pricingMode === 'tax_inclusive' 
                      ? 'LLM输出的价格为含税价，系统将自动计算未税价格'
                      : 'LLM输出的价格为未税价，系统将自动计算含税价格'}
                  </span>
                </div>

                {/* 全部确认按钮 */}
                <div className="mb-4 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleConfirmAll}
                    disabled={confirmedItems.size === previewData.items.length}
                    className="gap-1"
                  >
                    全部确认
                  </Button>
                </div>

                {/* 订单信息 */}
                <div className="mb-6 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">订单编号</label>
                    <Input
                      value={orderId}
                      onChange={(e) => setOrderId(e.target.value)}
                      placeholder="如: SO-2026-000123"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">订单日期</label>
                    <Input
                      type="date"
                      value={orderDate}
                      onChange={(e) => setOrderDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* 产品列表 */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>产品信息</TableHead>
                        <TableHead className="w-20">数量</TableHead>
                        <TableHead className="w-32">单价(含税/未税)</TableHead>
                        <TableHead className="w-28">匹配状态</TableHead>
                        <TableHead className="w-32">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.items?.map((item, idx) => {
                        const matchStatus = item.match_status;
                        const selectedId = selectedProducts[idx];
                        const matches = item.matches || [];
                        const isConfirmed = confirmedItems.has(idx);
                        const taxRate = item.tax_rate || 0.13;
                        const ocrPrice = item.unit_price || 0;
                        // Calculate prices based on pricing mode
                        const unitPrice = pricingMode === 'tax_inclusive' 
                          ? ocrPrice / (1 + taxRate)
                          : ocrPrice;
                        const unitPriceTax = pricingMode === 'tax_inclusive'
                          ? ocrPrice
                          : ocrPrice * (1 + taxRate);

                        return (
                          <TableRow key={idx} className={isConfirmed ? '' : 'bg-warning/5'}>
                            <TableCell className="font-mono text-sm">{idx + 1}</TableCell>
                            
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium flex items-center gap-2">
                                  {item.product_id || '—'}
                                  {selectedId && (
                                    <Badge variant="outline" className="text-xs">
                                      {matches.find(m => m._id === selectedId)?.name || '已选择'}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  规格: {item.specification || '—'}
                                </div>
                                {item.name && !selectedId && (
                                  <div className="text-sm text-muted-foreground">
                                    名称: {item.name}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            
                            <TableCell>
                              {item.quantity} {item.unit}
                            </TableCell>
                            
                            <TableCell className="font-medium">
                              <div className="text-sm">
                                <div className="text-primary font-semibold">
                                  {formatCurrency(unitPriceTax)}
                                </div>
                                <div className="text-muted-foreground text-xs">
                                  未税: {formatCurrency(unitPrice)}
                                </div>
                              </div>
                            </TableCell>
                            
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getMatchStatusBadge(matchStatus, item, idx)}
                                {!isConfirmed && (matchStatus === 'exact' || matchStatus === 'by_spec') && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 text-xs text-success hover:text-success px-2"
                                    onClick={() => {
                                      // Auto-select first match if not already selected
                                      if (!selectedProducts[idx] && item.matches?.[0]?._id) {
                                        setSelectedProducts(prev => ({
                                          ...prev,
                                          [idx]: item.matches[0]._id
                                        }));
                                      }
                                      handleConfirmItem(idx);
                                    }}
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    确认
                                  </Button>
                                )}
                              </div>
                              {item.note && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {item.note}
                                </div>
                              )}
                            </TableCell>
                            
                            <TableCell>
                              <div className="flex gap-1">
                                {isConfirmed ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs text-success hover:text-success"
                                    onClick={() => handleUnconfirmItem(idx)}
                                  >
                                    ✓ 已确认
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      onClick={() => handleOpenManualMatch(item, idx)}
                                    >
                                      <Search className="h-3 w-3 mr-1" />
                                      匹配
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      onClick={() => handleOpenCreateDrawer(item, idx)}
                                    >
                                      <Plus className="h-3 w-3 mr-1" />
                                      创建
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs text-muted-foreground hover:text-destructive"
                                  onClick={() => handleDeleteItem(idx)}
                                  title="删除此行"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="px-6 py-4 border-t bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {!selectedClient && '请选择客户'}
                    {selectedClient && !canConfirm() && '请处理所有产品匹配'}
                    {selectedClient && canConfirm() && '可以确认导入'}
                  </div>
                  <Button
                    onClick={handleConfirm}
                    disabled={!canConfirm() || creatingOrder}
                    className="gap-2"
                  >
                    {creatingOrder ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        正在创建...
                      </>
                    ) : (
                      <>
                        确认导入
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-success" />
                </div>
                <div className="text-xl font-medium">
                  {mode === 'sales_order' ? '销售订单' : '报价单'}创建成功
                </div>
                <div className="text-muted-foreground">
                  已成功创建 {mode === 'sales_order' ? '销售订单' : '报价单'}，共计 {formatCurrency(calculateTotal())}
                </div>
                <div className="flex justify-center gap-2 pt-4">
                  <Button variant="outline" onClick={handleClose}>
                    关闭
                  </Button>
                  {onImportComplete && (
                    <Button onClick={handleClose}>
                      完成
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 创建产品Drawer */}
      <ProductCreationDrawer
        open={showCreateDrawer}
        onClose={() => {
          setShowCreateDrawer(false);
          setCurrentCreateItem(null);
        }}
        initialData={currentCreateItem}
        onProductCreated={handleProductCreated}
      />

      {/* 手动匹配Dialog - 复用EnhancedProductLookup */}
      <EnhancedProductLookup
        open={manualMatchDialog.open}
        onClose={() => setManualMatchDialog({ open: false, itemIndex: null, item: null })}
        onAddProducts={handleManualMatch}
        clientName={selectedClient?.name || ''}
        currentQuotationItems={[]}
        singleSelect={true}
        title="手动匹配产品"
        subtitle="选择产品后，将保留OCR提取的价格和规格信息"
        initialSearchQuery={manualMatchDialog.item ? (manualMatchDialog.item.product_id || manualMatchDialog.item.specification || manualMatchDialog.item.name || '') : ''}
        ocrExtractedData={manualMatchDialog.item || null}
        onOcrSearch={(ocrData) => {
          // Use specification or name for search
          const query = ocrData.specification || ocrData.name || '';
          setManualMatchDialog(prev => ({
            ...prev,
            searchQuery: query
          }));
        }}
      />
    </>
  );
};

export default SalesImportDialog;
